import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Plus, Trash2, TrendingUp, TrendingDown, ArrowLeftRight,
  PiggyBank, Banknote, DollarSign, Search, BookOpen,
  ChevronDown, ChevronUp, AlertTriangle, Link2, CheckCircle2, Edit2,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { MoneyTransaction, MoneyTxType, Bucket, Asset } from '../types';
import { Modal } from './ui/Modal';
import { formatVND, formatVNDAxis } from '../utils/format';
import { v4 } from '../utils/uuid';
import { SensitiveValue } from './ui/SensitiveValue';
import { getAssetCurrentValue, getAssetCostBasisFromLedger, computeWalletBalance, GLOBAL_WALLET_ID, CASH_WALLET_ASSET_ID } from '../utils/calculations';
import { TxNode } from '../types';
import { MoneyFlowReports } from './MoneyFlowReports';

// ── Type Config ────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<MoneyTxType, {
  label: string; color: string; bg: string;
  sign: '+' | '−' | '↔';
  Icon: React.FC<{ size?: number; color?: string; strokeWidth?: number }>;
  hint: string;
}> = {
  BUY:      { label: 'Mua vào',    color: '#38bdf8', bg: 'rgba(56,189,248,0.1)',   sign: '+', Icon: TrendingUp,    hint: 'Mua tài sản / đầu tư mới' },
  SELL:     { label: 'Bán ra',     color: '#10b981', bg: 'rgba(16,185,129,0.1)',   sign: '−', Icon: TrendingDown,  hint: 'Bán tài sản / thoát vị thế' },
  INCOME:   { label: 'Thu nhập',   color: '#a3e635', bg: 'rgba(163,230,53,0.1)',   sign: '+', Icon: DollarSign,    hint: 'Cổ tức, lãi, tiền thuê nhận về' },
  TRANSFER: { label: 'Chuyển quỹ', color: '#a78bfa', bg: 'rgba(167,139,250,0.1)', sign: '↔', Icon: ArrowLeftRight, hint: 'Điều chuyển vốn trực tiếp quỹ A → quỹ B' },
  DEPOSIT:  { label: 'Nạp vốn',   color: '#fbbf24', bg: 'rgba(251,191,36,0.1)',   sign: '+', Icon: PiggyBank,     hint: 'Nạp tiền mới vào danh mục' },
  WITHDRAW: { label: 'Rút vốn',   color: '#f87171', bg: 'rgba(248,113,113,0.1)',  sign: '−', Icon: Banknote,      hint: 'Rút tiền ra khỏi danh mục' },
};

const HAS_ASSET_DETAIL: MoneyTxType[] = ['BUY', 'SELL', 'INCOME', 'DEPOSIT', 'WITHDRAW'];
const HAS_DEST_FUND:    MoneyTxType[] = ['TRANSFER'];

// ── Amount helper ──────────────────────────────────────────────────────────────

function parseRawAmount(raw: string): number {
  const s = raw.replace(/[,\s]/g, '').toLowerCase();
  const m = s.match(/^(\d+(?:\.\d+)?)(tỷ|ty|triệu|tr|trieu|nghìn|ngàn|k)?$/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  const u = m[2] ?? '';
  if (u.startsWith('tỷ') || u === 'ty')                                    return Math.round(n * 1_000_000_000);
  if (u.startsWith('tr') || u === 'triệu' || u === 'trieu')                return Math.round(n * 1_000_000);
  if (u === 'nghìn' || u === 'ngàn' || u === 'k')                          return Math.round(n * 1_000);
  if (n > 0 && n < 100_000)                                                 return Math.round(n * 1_000_000);
  return Math.round(n);
}

function today() { return new Date().toISOString().split('T')[0]; }
function monthKey(d: string) { return d.slice(0, 7); }
function currentMonth() { return today().slice(0, 7); }
function monthLabel(k: string) {
  const [y, m] = k.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('vi-VN', { month: 'short', year: 'numeric' });
}

// ── Form state ─────────────────────────────────────────────────────────────────

const emptyForm = (): Partial<MoneyTransaction> & {
  grossRaw: string; feesRaw: string; netRaw: string; qtyRaw: string; priceRaw: string;
} => ({
  type: 'BUY', asset_id: undefined, asset_name: '', quantity: undefined, price: undefined,
  gross_amount: undefined, fees: undefined, net_amount: 0,
  fund_id: GLOBAL_WALLET_ID, dest_fund_id: undefined,
  reason: '', note: '', date: today(),
  grossRaw: '', feesRaw: '', netRaw: '', qtyRaw: '', priceRaw: '',
});

type FormState = ReturnType<typeof emptyForm>;

// ── Sub-components ────────────────────────────────────────────────────────────

// ── Flow nodes config ─────────────────────────────────────────────────────────
const FLOW_SCHEMA: Record<MoneyTxType, { fromKind: 'wallet'|'asset'|'external'; toKind: 'wallet'|'asset'|'external' }> = {
  BUY:      { fromKind: 'wallet',   toKind: 'asset'    },
  SELL:     { fromKind: 'asset',    toKind: 'wallet'   },
  INCOME:   { fromKind: 'asset',    toKind: 'wallet'   },
  DEPOSIT:  { fromKind: 'external', toKind: 'wallet'   },
  WITHDRAW: { fromKind: 'wallet',   toKind: 'external' },
  TRANSFER: { fromKind: 'wallet',   toKind: 'wallet'   },
};

interface FlowNodeBoxProps {
  kind: 'wallet' | 'asset' | 'external';
  label: string;
  sub?: string;
  balance?: number | null;
  color?: string;
}
function FlowNodeBox({ kind, label, sub, balance, color = '#a78bfa' }: FlowNodeBoxProps) {
  const icon = kind === 'wallet' ? '🪙' : kind === 'asset' ? '📊' : '🌐';
  return (
    <div style={{
      flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 12,
      background: kind === 'external' ? 'rgba(100,116,139,0.08)' : `rgba(${kind === 'wallet' ? '167,139,250' : '56,189,248'},0.07)`,
      border: `1px solid ${kind === 'external' ? 'rgba(100,116,139,0.2)' : `rgba(${kind === 'wallet' ? '167,139,250' : '56,189,248'},0.25)`}`,
    }}>
      <div style={{ fontSize: 11, color: 'var(--tx-5)', marginBottom: 2 }}>{icon} {kind === 'wallet' ? 'Ví tiền mặt' : kind === 'asset' ? 'Tài sản' : 'Bên ngoài'}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label || '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>{sub}</div>}
      {balance !== undefined && balance !== null && (
        <div style={{ fontSize: 11, fontWeight: 600, color: balance >= 0 ? '#10b981' : '#f87171', marginTop: 2 }}>
          Ví: {formatVND(balance)}
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type }: { type: MoneyTxType }) {
  const c = TYPE_CONFIG[type];
  const { Icon } = c;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      color: c.color, background: c.bg,
    }}>
      <Icon size={11} strokeWidth={2.5} />{c.label}
    </span>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 5, display: 'block' }}>
      {children}
    </label>
  );
}

function FieldInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{
        width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13,
        background: 'var(--bg-input)', border: '1px solid var(--bd)',
        color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit',
        boxSizing: 'border-box', ...props.style,
      }}
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TransactionLogger({
  highlightTxId,
  onHighlightConsumed,
}: {
  highlightTxId?: string | null;
  onHighlightConsumed?: () => void;
} = {}) {
  const { state, dispatch } = useAppState();
  const { moneyTransactions = [], buckets = [], assets = [] } = state;
  const realBuckets = (buckets as Bucket[]).filter(b => b.id !== 'ruong-chua');

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterType,  setFilterType]  = useState<MoneyTxType | 'ALL'>('ALL');
  const [filterMonth, setFilterMonth] = useState<string>(currentMonth());
  const [searchText,  setSearchText]  = useState('');

  // ── Form ─────────────────────────────────────────────────────────────────
  const [formOpen,         setFormOpen]         = useState(false);
  const [editTx,           setEditTx]           = useState<MoneyTransaction | null>(null);
  const [form,             setForm]             = useState<FormState>(emptyForm());
  const [submitting,       setSubmitting]       = useState(false);
  const [applyAssetEffect, setApplyAssetEffect] = useState(false);

  // ── Detail expand ─────────────────────────────────────────────────────────
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ── Auto-expand + scroll khi được navigate từ AssetManager ───────────────
  useEffect(() => {
    if (!highlightTxId) return;
    const tx = (moneyTransactions as MoneyTransaction[]).find(t => t.id === highlightTxId);
    if (!tx) return;

    // Reset tất cả filter để đảm bảo tx hiện ra
    setFilterType('ALL');
    setFilterMonth('ALL');
    setSearchText('');
    setExpandedId(highlightTxId);

    // Scroll sau khi React render lại với filter đã reset
    const id = highlightTxId;
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(`tx-row-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onHighlightConsumed?.();
      } else if (attempts++ < 10) {
        setTimeout(tryScroll, 80);
      }
    };
    setTimeout(tryScroll, 80);
  }, [highlightTxId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fund NAV (để validate TRANSFER / WITHDRAW) ────────────────────────────
  const fundNAV = useMemo(() => {
    const map: Record<string, number> = {};
    (assets as Asset[]).forEach(a => {
      const bid = a.bucketId || 'ruong-chua';
      map[bid] = (map[bid] || 0) + getAssetCurrentValue(a);
    });
    return map;
  }, [assets]);

  // ── Tìm tài sản khớp — ưu tiên asset_id (dropdown), fallback text match ────
  const matchedAsset = useMemo<Asset | null>(() => {
    if (!HAS_ASSET_DETAIL.includes(form.type!)) return null;
    const pool = (assets as Asset[]).filter(a => a.id !== CASH_WALLET_ASSET_ID);
    if (form.asset_id) return pool.find(a => a.id === form.asset_id) ?? null;
    if (!form.asset_name?.trim()) return null;
    const q = form.asset_name.trim().toLowerCase();
    return pool.find(a =>
      a.name.toLowerCase() === q || a.code.toLowerCase() === q
    ) ?? null;
  }, [form.asset_id, form.asset_name, form.type, assets]);

  // Danh sách tài sản đầu tư (loại trừ ví chung — ví không phải tài sản đầu tư)
  const investAssets = useMemo(() =>
    (assets as Asset[]).filter(a => a.id !== CASH_WALLET_ASSET_ID),
  [assets]);

  // ── Autocomplete suggestions ──────────────────────────────────────────────
  const assetSuggestions = useMemo(() => {
    const names = new Set([
      ...investAssets.map(a => a.name),
      ...moneyTransactions.filter(t => t.asset_name).map(t => t.asset_name!),
    ]);
    return Array.from(names).sort();
  }, [investAssets, moneyTransactions]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validationError = useMemo((): string | null => {
    const { type, fund_id, dest_fund_id, net_amount, quantity } = form;
    if (!net_amount) return null;

    // Khi sửa giao dịch cũ: chỉ kiểm tra ràng buộc cơ bản (tổng ròng > 0)
    if (editTx) return null;

    if (type === 'TRANSFER') {
      if (!fund_id)           return 'Vui lòng chọn quỹ nguồn.';
      if (!dest_fund_id)      return 'Vui lòng chọn quỹ đích.';
      if (fund_id === dest_fund_id) return 'Quỹ nguồn và quỹ đích không được trùng nhau.';
      const nav = fundNAV[fund_id] ?? 0;
      if (net_amount > nav)   return `Quỹ nguồn chỉ có ${formatVND(nav)} — không đủ để chuyển ${formatVND(net_amount)}.`;
    }

    if (type === 'WITHDRAW' && fund_id) {
      const nav = fundNAV[fund_id] ?? 0;
      if (net_amount > nav)   return `Quỹ chỉ có ${formatVND(nav)} — không đủ để rút ${formatVND(net_amount)}.`;
    }

    if (type === 'SELL' && matchedAsset && quantity) {
      if (quantity > matchedAsset.quantity)
        return `Số lượng bán (${quantity.toLocaleString()}) vượt quá đang nắm giữ (${matchedAsset.quantity.toLocaleString()}).`;
    }

    return null;
  }, [form, fundNAV, matchedAsset, editTx]);

  // ── Computed ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => moneyTransactions.filter(tx => {
    if (filterType !== 'ALL' && tx.type !== filterType) return false;
    if (filterMonth !== 'ALL' && monthKey(tx.date) !== filterMonth) return false;
    if (searchText) {
      const s = searchText.toLowerCase();
      if (![tx.asset_name, tx.reason, tx.note].some(f => f?.toLowerCase().includes(s))) return false;
    }
    return true;
  }), [moneyTransactions, filterType, filterMonth, searchText]);

  const thisMonthTxs = useMemo(() =>
    moneyTransactions.filter(t => monthKey(t.date) === currentMonth()), [moneyTransactions]);

  const kpi = useMemo(() => ({
    deployed: thisMonthTxs.filter(t => t.type === 'BUY').reduce((s, t) => s + t.net_amount, 0),
    received: thisMonthTxs.filter(t => ['SELL', 'INCOME'].includes(t.type)).reduce((s, t) => s + t.net_amount, 0),
    count:    thisMonthTxs.length,
  }), [thisMonthTxs]);

  const chartData = useMemo(() => {
    const months: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      months.push(d.toISOString().slice(0, 7));
    }
    return months.map(mk => ({
      month: monthLabel(mk),
      'Triển khai': moneyTransactions.filter(t => monthKey(t.date) === mk && t.type === 'BUY').reduce((s, t) => s + t.net_amount, 0),
      'Thu về':     moneyTransactions.filter(t => monthKey(t.date) === mk && ['SELL','INCOME'].includes(t.type)).reduce((s, t) => s + t.net_amount, 0),
    }));
  }, [moneyTransactions]);

  const availableMonths = useMemo(() => {
    const keys = new Set([...moneyTransactions.map(t => monthKey(t.date)), currentMonth()]);
    return Array.from(keys).sort().reverse().slice(0, 12);
  }, [moneyTransactions]);

  const groupedByDate = useMemo(() => {
    const map = new Map<string, MoneyTransaction[]>();
    for (const tx of filtered) {
      if (!map.has(tx.date)) map.set(tx.date, []);
      map.get(tx.date)!.push(tx);
    }
    return Array.from(map.entries()).map(([date, txs]) => ({ date, txs }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [filtered]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const f = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm(p => ({ ...p, [k]: v })), []);

  const recalcGross = useCallback((qty: number, price: number, fees: number) => {
    if (qty > 0 && price > 0) {
      const gross = Math.round(qty * price);
      const net   = Math.max(0, gross - (fees || 0));
      setForm(p => ({
        ...p, gross_amount: gross, net_amount: net,
        grossRaw: String(gross / 1_000_000), netRaw: String(net / 1_000_000),
      }));
    }
  }, []);

  const handleQtyChange = (raw: string) => {
    f('qtyRaw', raw);
    const qty = parseFloat(raw) || 0;
    f('quantity', qty || undefined);
    recalcGross(qty, form.price || 0, form.fees || 0);
  };

  const handlePriceChange = (raw: string) => {
    f('priceRaw', raw);
    const price = parseRawAmount(raw);
    f('price', price || undefined);
    recalcGross(form.quantity || 0, price, form.fees || 0);
  };

  const handleGrossChange = (raw: string) => {
    f('grossRaw', raw);
    const gross = parseRawAmount(raw);
    f('gross_amount', gross || undefined);
    const net = Math.max(0, gross - (form.fees || 0));
    setForm(p => ({ ...p, net_amount: net, netRaw: net > 0 ? String(net / 1_000_000) : '' }));
  };

  const handleFeesChange = (raw: string) => {
    f('feesRaw', raw);
    const fees = parseRawAmount(raw);
    f('fees', fees || undefined);
    const gross = form.gross_amount || 0;
    const net   = Math.max(0, gross - fees);
    setForm(p => ({ ...p, net_amount: net, netRaw: net > 0 ? String(net / 1_000_000) : '' }));
  };

  const handleNetChange = (raw: string) => {
    f('netRaw', raw);
    const net = parseRawAmount(raw);
    f('net_amount', net);
  };

  const openForm = () => { setEditTx(null); setForm(emptyForm()); setFormOpen(true); };

  const openEdit = (tx: MoneyTransaction) => {
    setEditTx(tx);
    setForm({
      type:         tx.type,
      asset_id:     tx.asset_id,
      asset_name:   tx.asset_name   || '',
      quantity:     tx.quantity,
      price:        tx.price,
      gross_amount: tx.gross_amount,
      fees:         tx.fees,
      net_amount:   tx.net_amount,
      fund_id:      tx.fund_id,
      dest_fund_id: tx.dest_fund_id,
      reason:       tx.reason       || '',
      note:         tx.note         || '',
      date:         tx.date,
      grossRaw: tx.gross_amount ? String(tx.gross_amount / 1_000_000) : '',
      feesRaw:  tx.fees         ? String(tx.fees         / 1_000_000) : '',
      netRaw:   tx.net_amount   ? String(tx.net_amount   / 1_000_000) : '',
      qtyRaw:   tx.quantity     ? String(tx.quantity)                  : '',
      priceRaw: tx.price        ? String(tx.price        / 1_000_000) : '',
    });
    setApplyAssetEffect(false);
    setFormOpen(true);
  };

  // ── Số dư ví chung — lấy từ wallet asset (không tính lại formula) ─────────
  const walletAsset = useMemo(() =>
    (assets as Asset[]).find(a => a.id === CASH_WALLET_ASSET_ID),
  [assets]);
  const globalWalletBalance = walletAsset?.currentPrice ?? 0;

  // ── Wallet balance cho form (global hoặc per-fund với TRANSFER) ───────────
  const walletBalance = useMemo(() => {
    if (form.type === 'TRANSFER') {
      if (!form.fund_id) return null;
      return computeWalletBalance(form.fund_id, moneyTransactions as MoneyTransaction[]);
    }
    return globalWalletBalance;
  }, [form.type, form.fund_id, globalWalletBalance, moneyTransactions]);

  const destWalletBalance = useMemo(() => {
    if (!form.dest_fund_id) return null;
    return computeWalletBalance(form.dest_fund_id, moneyTransactions as MoneyTransaction[]);
  }, [form.dest_fund_id, moneyTransactions]);

  // ── Submit ────────────────────────────────────────────────────────────────

  // ── Tính from_node/to_node từ form ──────────────────────────────────────
  const buildNodes = (type: string, assetId?: string, fundId?: string, destFundId?: string): { from_node: TxNode; to_node: TxNode } => {
    const globalWallet: TxNode = { kind: 'wallet', fund_id: GLOBAL_WALLET_ID };
    const fundWallet   = (fid?: string): TxNode => ({ kind: 'wallet',  fund_id:  fid });
    const assetNode    = (aid?: string): TxNode => ({ kind: 'asset',   asset_id: aid });
    const external: TxNode = { kind: 'external' };
    switch (type) {
      case 'BUY':      return { from_node: globalWallet,                                    to_node: assetId ? assetNode(assetId) : external };
      case 'SELL':     return { from_node: assetId ? assetNode(assetId) : external,         to_node: globalWallet };
      case 'INCOME':   return { from_node: assetId ? assetNode(assetId) : external,         to_node: globalWallet };
      case 'DEPOSIT':  return { from_node: external,                                        to_node: globalWallet };
      case 'WITHDRAW': return { from_node: globalWallet,                                    to_node: external };
      case 'TRANSFER': return { from_node: fundWallet(fundId),                              to_node: fundWallet(destFundId) };
      default:         return { from_node: external,                                        to_node: external };
    }
  };

  const handleSubmit = () => {
    if ((form.net_amount ?? 0) <= 0 || !form.date || validationError) return;
    setSubmitting(true);

    const resolvedAssetId = matchedAsset?.id || form.asset_id || undefined;
    // fund_id: dùng cho phân loại/hiển thị; ví tiền mặt luôn là global wallet (trừ TRANSFER)
    const resolvedFundId = form.type === 'TRANSFER' ? form.fund_id : (matchedAsset?.bucketId || form.fund_id || GLOBAL_WALLET_ID);
    const nodes = buildNodes(form.type!, resolvedAssetId, form.type === 'TRANSFER' ? form.fund_id : undefined, form.dest_fund_id);

    if (editTx) {
      // ── Chế độ sửa — chỉ cập nhật số lượng/currentPrice (KHÔNG costPrice — dùng ledger)
      const updated: MoneyTransaction = {
        ...editTx, ...nodes,
        type:         form.type!,
        asset_id:     resolvedAssetId,
        asset_name:   form.asset_name   || undefined,
        quantity:     form.quantity,
        price:        form.price,
        gross_amount: form.gross_amount,
        fees:         form.fees,
        net_amount:   form.net_amount!,
        fund_id:      resolvedFundId    || undefined,
        dest_fund_id: form.dest_fund_id || undefined,
        reason:       form.reason       || '',
        note:         form.note         || undefined,
        date:         form.date!,
      };
      dispatch({ type: 'UPDATE_MONEY_TX', payload: updated });

      if (applyAssetEffect && matchedAsset) {
        let newCurrentPrice = matchedAsset.currentPrice;
        let newQty          = matchedAsset.quantity;

        const wasDeposit  = ['BUY', 'DEPOSIT'].includes(editTx.type);
        const wasWithdraw = ['SELL', 'WITHDRAW'].includes(editTx.type);
        if (wasDeposit)  { newCurrentPrice -= editTx.net_amount;  newQty -= (editTx.quantity || 0); }
        if (wasWithdraw) { newCurrentPrice += editTx.net_amount;  newQty += (editTx.quantity || 0); }

        const isDeposit  = ['BUY', 'DEPOSIT'].includes(updated.type);
        const isWithdraw = ['SELL', 'WITHDRAW'].includes(updated.type);
        if (isDeposit)  { newCurrentPrice += updated.net_amount;  newQty += (updated.quantity || 0); }
        if (isWithdraw) { newCurrentPrice -= updated.net_amount;  newQty -= (updated.quantity || 0); }

        const finalQty = Math.max(0, newQty);
        // Recalculate giá vốn từ ledger với tx đã được sửa
        const newTxs     = (moneyTransactions as MoneyTransaction[]).map(t => t.id === updated.id ? updated : t);
        const totalCost  = getAssetCostBasisFromLedger(matchedAsset.id, newTxs);
        const newCostPrice = finalQty > 0 ? totalCost / finalQty : 0;

        dispatch({ type: 'UPDATE_ASSET', payload: {
          ...matchedAsset,
          quantity:     finalQty,
          currentPrice: Math.max(0, newCurrentPrice),
          costPrice:    newCostPrice,
        }});
      }

      // ── Cập nhật ví chung (revert cũ + apply mới) ───────────────────────
      if (walletAsset && editTx.type !== 'TRANSFER' && updated.type !== 'TRANSFER') {
        const oldIsOriginBuy = editTx.type === 'BUY' && editTx.reason === 'Giao dịch gốc';
        const newIsOriginBuy = updated.type === 'BUY' && updated.reason === 'Giao dịch gốc';
        let walletDelta = 0;
        if (!oldIsOriginBuy) {
          walletDelta += ['DEPOSIT', 'SELL', 'INCOME'].includes(editTx.type) ? -editTx.net_amount : editTx.net_amount;
        }
        if (!newIsOriginBuy) {
          walletDelta += ['DEPOSIT', 'SELL', 'INCOME'].includes(updated.type) ? updated.net_amount : -updated.net_amount;
        }
        if (walletDelta !== 0) {
          dispatch({ type: 'UPDATE_ASSET', payload: {
            ...walletAsset, currentPrice: walletAsset.currentPrice + walletDelta,
          }});
        }
      }
    } else {
      // ── Chế độ thêm mới — không mutate costPrice (ledger là nguồn sự thật)
      const tx: MoneyTransaction = {
        id: v4(), ...nodes,
        type:         form.type!,
        asset_id:     resolvedAssetId,
        asset_name:   form.asset_name    || undefined,
        quantity:     form.quantity,
        price:        form.price,
        gross_amount: form.gross_amount,
        fees:         form.fees,
        net_amount:   form.net_amount!,
        fund_id:      resolvedFundId      || undefined,
        dest_fund_id: form.dest_fund_id  || undefined,
        reason:       form.reason        || '',
        note:         form.note          || undefined,
        date:         form.date!,
      };
      dispatch({ type: 'ADD_MONEY_TX', payload: tx });

      // Cập nhật tài sản liên kết — kể cả costPrice tính từ ledger
      if (matchedAsset) {
        const isDeposit  = ['BUY', 'DEPOSIT'].includes(tx.type);
        const isWithdraw = ['SELL', 'WITHDRAW'].includes(tx.type);
        if (isDeposit || isWithdraw) {
          const newQty = isDeposit
            ? matchedAsset.quantity + (tx.quantity || 0)
            : Math.max(0, matchedAsset.quantity - (tx.quantity || 0));
          const newCurrentPrice = isDeposit
            ? matchedAsset.currentPrice + tx.net_amount
            : Math.max(0, matchedAsset.currentPrice - tx.net_amount);
          // Recalculate giá vốn từ ledger (ledger là nguồn sự thật)
          const newTxs      = [tx, ...(moneyTransactions as MoneyTransaction[])];
          const totalCost   = getAssetCostBasisFromLedger(matchedAsset.id, newTxs);
          const newCostPrice = newQty > 0 ? totalCost / newQty : 0;
          dispatch({ type: 'UPDATE_ASSET', payload: {
            ...matchedAsset,
            quantity:     newQty,
            currentPrice: newCurrentPrice,
            costPrice:    newCostPrice,
          }});
        }
      }

      // ── Cập nhật ví chung ────────────────────────────────────────────────
      if (walletAsset && tx.type !== 'TRANSFER') {
        const isOriginBuy = tx.type === 'BUY' && tx.reason === 'Giao dịch gốc';
        if (!isOriginBuy) {
          const walletDelta = ['DEPOSIT', 'SELL', 'INCOME'].includes(tx.type)
            ? tx.net_amount : -tx.net_amount;
          dispatch({ type: 'UPDATE_ASSET', payload: {
            ...walletAsset, currentPrice: walletAsset.currentPrice + walletDelta,
          }});
        }
      }
    }

    setFormOpen(false);
    setEditTx(null);
    setForm(emptyForm());
    setApplyAssetEffect(false);
    setSubmitting(false);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Xoá giao dịch này?')) return;
    const tx = (moneyTransactions as MoneyTransaction[]).find(t => t.id === id);
    dispatch({ type: 'DELETE_MONEY_TX', payload: id });

    // Revert hiệu ứng lên giá vốn tài sản — chỉ với các tx thường (không phải giao dịch gốc ban đầu)
    if (tx?.asset_id && tx.reason !== 'Giao dịch gốc') {
      const linkedAsset = (assets as Asset[]).find(a => a.id === tx.asset_id);
      if (linkedAsset) {
        const wasDeposit  = ['BUY', 'DEPOSIT'].includes(tx.type);
        const wasWithdraw = ['SELL', 'WITHDRAW'].includes(tx.type);
        if (wasDeposit || wasWithdraw) {
          const newQty = wasDeposit
            ? Math.max(0, linkedAsset.quantity - (tx.quantity || 0))
            : linkedAsset.quantity + (tx.quantity || 0);
          const newCurrentPrice = wasDeposit
            ? Math.max(0, linkedAsset.currentPrice - tx.net_amount)
            : linkedAsset.currentPrice + tx.net_amount;
          // Recalculate giá vốn từ ledger sau khi xóa tx
          const newTxs     = (moneyTransactions as MoneyTransaction[]).filter(t => t.id !== id);
          const totalCost  = getAssetCostBasisFromLedger(linkedAsset.id, newTxs);
          const newCostPrice = newQty > 0 ? totalCost / newQty : 0;
          dispatch({ type: 'UPDATE_ASSET', payload: {
            ...linkedAsset,
            quantity:     newQty,
            currentPrice: newCurrentPrice,
            costPrice:    newCostPrice,
          }});
        }
      }
    }

    // ── Revert ví chung ───────────────────────────────────────────────────
    if (tx && walletAsset && tx.type !== 'TRANSFER') {
      const isOriginBuy = tx.type === 'BUY' && tx.reason === 'Giao dịch gốc';
      if (!isOriginBuy) {
        // Revert = ngược chiều với lúc tạo
        const walletDelta = ['DEPOSIT', 'SELL', 'INCOME'].includes(tx.type)
          ? -tx.net_amount : tx.net_amount;
        dispatch({ type: 'UPDATE_ASSET', payload: {
          ...walletAsset, currentPrice: walletAsset.currentPrice + walletDelta,
        }});
      }
    }
  };

  // ── Tạo giao dịch gốc ────────────────────────────────────────────────────
  const [originModalOpen, setOriginModalOpen] = useState(false);

  // Với mỗi tài sản: tính phần giá vốn chưa có giao dịch nào ghi nhận
  const assetsNeedingOrigin = useMemo(() => {
    return (assets as Asset[])
      .filter(a => a.id !== CASH_WALLET_ASSET_ID && a.costPrice > 0 &&
        !moneyTransactions.some(t => t.asset_id === a.id && t.reason === 'Giao dịch gốc')
      )
      .map(a => {
        const nameL   = a.name.toLowerCase();
        const isLinked = (t: MoneyTransaction) =>
          t.asset_id === a.id || t.asset_name?.toLowerCase() === nameL || t.asset_name?.toLowerCase() === a.code.toLowerCase();
        const deposited = moneyTransactions
          .filter(t => isLinked(t) && ['BUY', 'DEPOSIT'].includes(t.type))
          .reduce((s, t) => s + t.net_amount, 0);
        const withdrawn = moneyTransactions
          .filter(t => isLinked(t) && ['SELL', 'WITHDRAW'].includes(t.type))
          .reduce((s, t) => s + t.net_amount, 0);
        const originAmount = Math.round(a.costPrice - deposited + withdrawn);
        return { asset: a, originAmount };
      })
      .filter(({ originAmount }) => originAmount > 0);
  }, [assets, moneyTransactions]);

  const handleCreateOrigins = () => {
    assetsNeedingOrigin.forEach(({ asset, originAmount }) => {
      dispatch({ type: 'ADD_MONEY_TX', payload: {
        id:         v4(),
        type:       'BUY',
        from_node:  { kind: 'external' },   // vốn ban đầu từ bên ngoài, không qua ví
        to_node:    { kind: 'asset',  asset_id: asset.id },
        asset_id:   asset.id,
        asset_name: asset.name,
        net_amount: originAmount,
        quantity:   asset.quantity > 0 ? asset.quantity : undefined,
        fund_id:    asset.bucketId || GLOBAL_WALLET_ID,
        date:       '2026-01-01',
        reason:     'Giao dịch gốc',
        note:       `Giá vốn ban đầu — ${asset.name}`,
      }});
    });
    setOriginModalOpen(false);
  };

  // ── Chart tooltip ─────────────────────────────────────────────────────────
  const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ color: 'var(--tx-3)', marginBottom: 6 }}>{label}</div>
        {payload.map((p: any) => (
          <div key={p.name} style={{ color: p.fill, fontWeight: 600 }}>{p.name}: {formatVND(p.value)}</div>
        ))}
      </div>
    );
  };

  // ── canSubmit ─────────────────────────────────────────────────────────────
  const canSubmit = (form.net_amount ?? 0) > 0 && !validationError && !submitting;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900, margin: '0 auto' }}>

      {/* ── KPI ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160, borderRadius: 14, padding: '14px 16px', background: 'var(--bg-card)', border: `1px solid ${globalWalletBalance >= 0 ? 'rgba(167,139,250,0.25)' : 'rgba(248,113,113,0.25)'}` }}>
          <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>🪙 Số dư ví chung</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: globalWalletBalance >= 0 ? '#a78bfa' : '#f87171', lineHeight: 1 }}>
            <SensitiveValue>{formatVND(globalWalletBalance)}</SensitiveValue>
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 4 }}>Tiền mặt khả dụng</div>
        </div>
        {[
          { label: 'Vốn triển khai (tháng này)', value: kpi.deployed, color: '#38bdf8', sub: `${thisMonthTxs.filter(t => t.type === 'BUY').length} lệnh mua` },
          { label: 'Thu về (tháng này)',          value: kpi.received, color: '#10b981', sub: 'Bán + Thu nhập' },
          { label: 'Tổng giao dịch',              value: kpi.count,   color: '#a78bfa', sub: 'tháng này', isCount: true },
        ].map(({ label, value, color, sub, isCount }) => (
          <div key={label} style={{ flex: 1, minWidth: 140, borderRadius: 14, padding: '14px 16px', background: 'var(--bg-card)', border: `1px solid ${color}22` }}>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>
              {isCount ? value : <SensitiveValue>{formatVND(value as number)}</SensitiveValue>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 4 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* ── Báo cáo dòng vốn chi tiết ── */}
      {moneyTransactions.length > 0 && (
        <div style={{ borderRadius: 16, padding: '18px 16px', background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
          <MoneyFlowReports />
        </div>
      )}

      {/* ── Filter + Add ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-4)' }} />
          <input value={searchText} onChange={e => setSearchText(e.target.value)}
            placeholder="Tìm tài sản, lý do..."
            style={{ width: '100%', padding: '9px 12px 9px 32px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        {[
          { v: 'ALL', l: 'Tất cả' }, { v: 'BUY', l: 'Mua vào' }, { v: 'SELL', l: 'Bán ra' },
          { v: 'INCOME', l: 'Thu nhập' }, { v: 'TRANSFER', l: 'Chuyển quỹ' },
          { v: 'DEPOSIT', l: 'Nạp vốn' }, { v: 'WITHDRAW', l: 'Rút vốn' },
        ].map(({ v, l }) => (
          <button key={v} onClick={() => setFilterType(v as any)} style={{
            padding: '7px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
            background: filterType === v ? 'rgba(16,185,129,0.12)' : 'var(--bg-card-el)',
            border: `1px solid ${filterType === v ? 'rgba(16,185,129,0.3)' : 'var(--bd)'}`,
            color: filterType === v ? '#10b981' : 'var(--tx-3)', fontWeight: filterType === v ? 600 : 400,
            flexShrink: 0,
          }}>{l}</button>
        ))}
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
          style={{ padding: '9px 10px', borderRadius: 10, fontSize: 12, background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-2)', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
          <option value="ALL">Tất cả tháng</option>
          {availableMonths.map(mk => <option key={mk} value={mk}>{monthLabel(mk)}</option>)}
        </select>
        {assetsNeedingOrigin.length > 0 && (
          <button onClick={() => setOriginModalOpen(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
            fontSize: 12, fontWeight: 600, flexShrink: 0,
            background: 'rgba(251,191,36,0.12)', color: '#fbbf24',
            border: '1px solid rgba(251,191,36,0.3)', cursor: 'pointer',
          }}>
            <BookOpen size={13} strokeWidth={2} />{assetsNeedingOrigin.length} giao dịch gốc
          </button>
        )}
        <button onClick={openForm} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10,
          fontSize: 13, fontWeight: 600, flexShrink: 0,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 2px 12px rgba(16,185,129,0.3)',
        }}>
          <Plus size={14} strokeWidth={2.5} />Ghi giao dịch
        </button>
      </div>

      {/* ── List ── */}
      {groupedByDate.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 6 }}>Chưa có giao dịch nào</div>
          <div style={{ fontSize: 13, color: 'var(--tx-4)', marginBottom: 20 }}>
            Ghi nhận mua bán, cổ tức, nạp vốn — kèm lý do để phân tích sau này
          </div>
          <button onClick={openForm} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff', border: 'none', cursor: 'pointer',
          }}>
            <Plus size={14} /> Ghi giao dịch đầu tiên
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groupedByDate.map(({ date, txs }) => (
            <div key={date}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 7, padding: '0 2px',
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span>{new Date(date + 'T00:00:00').toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                <span style={{ fontWeight: 400 }}>{txs.length} giao dịch</span>
              </div>
              <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--bd)', background: 'var(--bg-card)' }}>
                {txs.map((tx, i) => {
                  const cfg = TYPE_CONFIG[tx.type];
                  const { Icon } = cfg;
                  const isExpanded = expandedId === tx.id;
                  const fund     = (buckets as Bucket[]).find(b => b.id === tx.fund_id);
                  const destFund = (buckets as Bucket[]).find(b => b.id === tx.dest_fund_id);
                  const linkedAsset = tx.asset_id ? (assets as Asset[]).find(a => a.id === tx.asset_id) : null;
                  return (
                    <div key={tx.id} id={`tx-row-${tx.id}`}
                      style={{ borderTop: i > 0 ? '1px solid var(--bd-sub)' : 'none',
                        boxShadow: highlightTxId === tx.id && isExpanded ? 'inset 0 0 0 2px rgba(167,139,250,0.4)' : 'none',
                        borderRadius: highlightTxId === tx.id && isExpanded ? 10 : 0,
                        transition: 'box-shadow 0.3s',
                      }}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', cursor: 'pointer', transition: 'background 0.12s' }}
                        onClick={() => setExpandedId(isExpanded ? null : tx.id)}
                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-row-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      >
                        <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon size={16} color={cfg.color} strokeWidth={2} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <TypeBadge type={tx.type} />
                            {tx.asset_name && (
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{tx.asset_name}</span>
                            )}
                            {linkedAsset && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', padding: '2px 6px', borderRadius: 5 }}>
                                <Link2 size={9} /> Liên kết danh mục
                              </span>
                            )}
                            {tx.quantity && tx.price && (
                              <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>
                                {tx.quantity.toLocaleString()} × {formatVND(tx.price)}
                              </span>
                            )}
                          </div>
                          {tx.reason && (
                            <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <BookOpen size={11} style={{ flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                                {tx.reason}
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: cfg.color }}>
                              {cfg.sign !== '↔' ? cfg.sign : ''} <SensitiveValue>{formatVND(tx.net_amount)}</SensitiveValue>
                            </div>
                            {tx.fees ? <div style={{ fontSize: 11, color: 'var(--tx-5)' }}>phí {formatVND(tx.fees)}</div> : null}
                          </div>
                          <span style={{ color: 'var(--tx-5)' }}>
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: '0 14px 14px 62px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            {tx.gross_amount ? (
                              <div><span style={{ fontSize: 11, color: 'var(--tx-5)' }}>Tổng thô: </span><span style={{ fontSize: 12, color: 'var(--tx-2)' }}>{formatVND(tx.gross_amount)}</span></div>
                            ) : null}
                            {tx.fees ? (
                              <div><span style={{ fontSize: 11, color: 'var(--tx-5)' }}>Phí: </span><span style={{ fontSize: 12, color: '#f87171' }}>−{formatVND(tx.fees)}</span></div>
                            ) : null}
                            {fund && (
                              <div><span style={{ fontSize: 11, color: 'var(--tx-5)' }}>Quỹ: </span><span style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fund.name}</span></div>
                            )}
                            {destFund && (
                              <div><span style={{ fontSize: 11, color: 'var(--tx-5)' }}>→ Quỹ đích: </span><span style={{ fontSize: 12, color: 'var(--tx-2)' }}>{destFund.name}</span></div>
                            )}
                            {linkedAsset && (
                              <div><span style={{ fontSize: 11, color: 'var(--tx-5)' }}>Tài sản: </span><span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>{linkedAsset.name}</span></div>
                            )}
                          </div>
                          {tx.note && <div style={{ fontSize: 12, color: 'var(--tx-4)', fontStyle: 'italic' }}>{tx.note}</div>}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={e => { e.stopPropagation(); openEdit(tx); }} style={{
                              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7,
                              fontSize: 12, cursor: 'pointer', background: 'transparent',
                              border: '1px solid var(--bd)', color: 'var(--tx-4)', transition: 'all 0.12s',
                            }}
                              onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'rgba(56,189,248,0.08)'; b.style.color = '#38bdf8'; b.style.borderColor = 'rgba(56,189,248,0.3)'; }}
                              onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'transparent'; b.style.color = 'var(--tx-4)'; b.style.borderColor = 'var(--bd)'; }}
                            >
                              <Edit2 size={12} />Sửa
                            </button>
                            <button onClick={e => { e.stopPropagation(); handleDelete(tx.id); }} style={{
                              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7,
                              fontSize: 12, cursor: 'pointer', background: 'transparent',
                              border: '1px solid var(--bd)', color: 'var(--tx-4)', transition: 'all 0.12s',
                            }}
                              onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'rgba(248,113,113,0.1)'; b.style.color = '#f87171'; b.style.borderColor = 'rgba(248,113,113,0.3)'; }}
                              onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'transparent'; b.style.color = 'var(--tx-4)'; b.style.borderColor = 'var(--bd)'; }}
                            >
                              <Trash2 size={12} />Xoá
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Origin Transactions Modal ── */}
      <Modal open={originModalOpen} onClose={() => setOriginModalOpen(false)}
        title="Tạo giao dịch gốc" subtitle="Ghi nhận lịch sử hình thành tài sản · Ngày 01/01/2026" size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--tx-3)', padding: '10px 14px', borderRadius: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)' }}>
            Hệ thống sẽ tạo <b style={{ color: 'var(--tx-1)' }}>{assetsNeedingOrigin.length} giao dịch BUY</b> đại diện cho giá vốn ban đầu của từng tài sản (phần chưa có giao dịch nào ghi nhận). Các giao dịch này <b>không thay đổi</b> giá trị tài sản hiện tại.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
            {assetsNeedingOrigin.map(({ asset, originAmount }) => (
              <div key={asset.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-card-el)', border: '1px solid var(--bd-sub)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{asset.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>{asset.quantity > 0 ? `${asset.quantity.toLocaleString()} đơn vị` : ''}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>{formatVND(originAmount)}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 2 }}>01/01/2026</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4, borderTop: '1px solid var(--bd)' }}>
            <button onClick={() => setOriginModalOpen(false)}
              style={{ padding: '9px 18px', borderRadius: 10, fontSize: 13, cursor: 'pointer', background: 'var(--bg-card-el)', border: '1px solid var(--bd)', color: 'var(--tx-3)' }}>
              Hủy
            </button>
            <button onClick={handleCreateOrigins}
              style={{ padding: '9px 22px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#fbbf24,#d97706)', color: '#000', border: 'none' }}>
              Tạo {assetsNeedingOrigin.length} giao dịch gốc
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Form Modal ── */}
      <Modal open={formOpen} onClose={() => { setFormOpen(false); setEditTx(null); }}
        title={editTx ? 'Sửa giao dịch' : 'Ghi giao dịch đầu tư'}
        subtitle={editTx ? `ID: ${editTx.id.slice(0, 8)}… · Thay đổi không hoàn tác số lượng tài sản` : 'Chi tiết giao dịch · Lý do quyết định'}
        size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ── 4-Pillar Quick Actions ── */}
          <div>
            <FieldLabel>Loại giao dịch</FieldLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {([
                { type: 'DEPOSIT'  as MoneyTxType, emoji: '💰', label: 'Nạp tiền vào ví',     desc: 'Lương, thưởng, tiền từ ngoài' },
                { type: 'BUY'      as MoneyTxType, emoji: '📊', label: 'Đầu tư tài sản',      desc: 'Mua tài sản từ ví' },
                { type: 'INCOME'   as MoneyTxType, emoji: '🌱', label: 'Thu hoạch',            desc: 'Cổ tức, lãi, bán bớt' },
                { type: 'WITHDRAW' as MoneyTxType, emoji: '💸', label: 'Chi dùng / Rút vốn',  desc: 'Chi tiêu, rút tiền ra ngoài' },
              ] as const).map(({ type, emoji, label, desc }) => {
                const isHarvest = type === 'INCOME';
                const active = isHarvest
                  ? (form.type === 'INCOME' || form.type === 'SELL')
                  : form.type === type;
                const cfg = TYPE_CONFIG[type];
                return (
                  <button key={type}
                    onClick={() => f('type', isHarvest ? (form.type === 'SELL' ? 'SELL' : 'INCOME') : type)}
                    style={{
                      padding: '12px 14px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                      background: active ? cfg.bg : 'var(--bg-card-el)',
                      border: `1.5px solid ${active ? cfg.color : 'var(--bd)'}`,
                      color: active ? cfg.color : 'var(--tx-4)',
                    }}
                  >
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{emoji}</div>
                    <div style={{ fontWeight: 700, marginBottom: 2, color: active ? cfg.color : 'var(--tx-2)' }}>{label}</div>
                    <div style={{ fontSize: 10, opacity: 0.8, fontWeight: 400, color: 'var(--tx-4)' }}>{desc}</div>
                  </button>
                );
              })}
            </div>

            {/* Thu hoạch sub-tabs: INCOME vs SELL */}
            {(form.type === 'INCOME' || form.type === 'SELL') && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={() => f('type', 'INCOME')} style={{
                  flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: form.type === 'INCOME' ? TYPE_CONFIG.INCOME.bg : 'var(--bg-card-el)',
                  border: `1px solid ${form.type === 'INCOME' ? TYPE_CONFIG.INCOME.color : 'var(--bd)'}`,
                  color: form.type === 'INCOME' ? TYPE_CONFIG.INCOME.color : 'var(--tx-4)',
                }}>🎯 Cổ tức / Lãi / Tiền thuê</button>
                <button onClick={() => f('type', 'SELL')} style={{
                  flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: form.type === 'SELL' ? TYPE_CONFIG.SELL.bg : 'var(--bg-card-el)',
                  border: `1px solid ${form.type === 'SELL' ? TYPE_CONFIG.SELL.color : 'var(--bd)'}`,
                  color: form.type === 'SELL' ? TYPE_CONFIG.SELL.color : 'var(--tx-4)',
                }}>📉 Bán bớt tài sản</button>
              </div>
            )}

            {/* TRANSFER — advanced */}
            <button onClick={() => f('type', 'TRANSFER')} style={{
              width: '100%', marginTop: 6, padding: '6px 10px', borderRadius: 8, fontSize: 11,
              cursor: 'pointer', textAlign: 'center', fontWeight: 500,
              background: form.type === 'TRANSFER' ? TYPE_CONFIG.TRANSFER.bg : 'transparent',
              border: `1px dashed ${form.type === 'TRANSFER' ? TYPE_CONFIG.TRANSFER.color : 'var(--bd)'}`,
              color: form.type === 'TRANSFER' ? TYPE_CONFIG.TRANSFER.color : 'var(--tx-5)',
            }}>↔ Chuyển quỹ nội bộ (nâng cao)</button>
          </div>

          {/* ── Dòng vốn: Nguồn → Đích ── */}
          {form.type && (() => {
            const schema    = FLOW_SCHEMA[form.type!];
            const isTransfer = form.type === 'TRANSFER';
            const fund      = realBuckets.find(b => b.id === form.fund_id);
            const dFund     = realBuckets.find(b => b.id === form.dest_fund_id);

            // Với TRANSFER: hiện tên quỹ per-fund; còn lại: luôn hiện "Ví chung"
            const walletLabel = (kind: 'from' | 'to') => {
              if (!isTransfer) return 'Ví chung';
              return kind === 'from' ? (fund?.name || 'Chọn quỹ nguồn…') : (dFund?.name || 'Chọn quỹ đích…');
            };
            const fromLabel = schema.fromKind === 'wallet' ? walletLabel('from') :
                              schema.fromKind === 'asset'  ? (matchedAsset?.name || form.asset_name || 'Chọn tài sản…') : 'Bên ngoài';
            const toLabel   = schema.toKind === 'wallet'   ? walletLabel('to') :
                              schema.toKind === 'asset'    ? (matchedAsset?.name || form.asset_name || 'Chọn tài sản…') : 'Bên ngoài';
            const fromSub = schema.fromKind === 'external' ? 'Lương, thưởng, thu nhập…' :
                            schema.fromKind === 'asset'    ? (matchedAsset ? `Giá hiện tại: ${formatVND(matchedAsset.currentPrice)}` : undefined) :
                            !isTransfer ? `Số dư: ${formatVND(globalWalletBalance)}` : undefined;
            const toSub   = schema.toKind === 'external'   ? 'Chi tiêu, rút dùng…' :
                            schema.toKind === 'asset'      ? (matchedAsset ? `Giá hiện tại: ${formatVND(matchedAsset.currentPrice)}` : undefined) :
                            !isTransfer ? `Số dư: ${formatVND(globalWalletBalance)}` : undefined;
            const fromBal = schema.fromKind === 'wallet' ? (isTransfer ? walletBalance : null) : undefined;
            const toBal   = schema.toKind === 'wallet'   ? (isTransfer ? destWalletBalance : null) : undefined;
            return (
              <div>
                <FieldLabel>Dòng vốn</FieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FlowNodeBox kind={schema.fromKind} label={fromLabel} sub={fromSub} balance={fromBal} />
                  <div style={{ fontSize: 18, color: 'var(--tx-4)', flexShrink: 0 }}>→</div>
                  <FlowNodeBox kind={schema.toKind} label={toLabel} sub={toSub} balance={toBal} />
                </div>
              </div>
            );
          })()}

          {/* Tài sản liên kết */}
          {HAS_ASSET_DETAIL.includes(form.type!) && (
            <div>
              <FieldLabel>Tài sản liên kết</FieldLabel>

              {/* DEPOSIT / WITHDRAW / INCOME → dropdown chọn tài sản có sẵn */}
              {['DEPOSIT', 'WITHDRAW', 'INCOME'].includes(form.type!) ? (
                <select
                  value={form.asset_id || ''}
                  onChange={e => {
                    const selectedId = e.target.value;
                    const a = investAssets.find(x => x.id === selectedId);
                    setForm(p => ({ ...p, asset_id: a?.id || undefined, asset_name: a?.name || '' }));
                  }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, background: 'var(--bg-input)', border: `1px solid ${matchedAsset ? 'rgba(167,139,250,0.5)' : 'var(--bd)'}`, color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                >
                  <option value="">— Không gán —</option>
                  {investAssets
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} · {formatVND(a.currentPrice)}
                      </option>
                    ))
                  }
                </select>
              ) : (
                /* BUY / SELL → text + datalist (có thể là tài sản mới chưa có trong danh mục) */
                <>
                  <input list="asset-suggest-list"
                    value={form.asset_name || ''}
                    onChange={e => f('asset_name', e.target.value)}
                    placeholder="VD: VNM, Gold SJC, Shophouse Q7, VFMVF1..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, background: 'var(--bg-input)', border: `1px solid ${matchedAsset ? 'rgba(167,139,250,0.5)' : 'var(--bd)'}`, color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                  {assetSuggestions.length > 0 && (
                    <datalist id="asset-suggest-list">
                      {assetSuggestions.map(n => <option key={n} value={n} />)}
                    </datalist>
                  )}
                </>
              )}

              {/* Thông báo liên kết */}
              {matchedAsset && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 8, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)' }}>
                  <CheckCircle2 size={13} color="#a78bfa" />
                  <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 500 }}>
                    Liên kết: <b>{matchedAsset.name}</b>
                    {' · '}Giá vốn (sổ cái): {formatVND(getAssetCostBasisFromLedger(matchedAsset.id, moneyTransactions as MoneyTransaction[]))}
                    {' · '}Giá hiện tại: {formatVND(matchedAsset.currentPrice)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Số lượng + Giá (BUY / SELL) */}
          {(form.type === 'BUY' || form.type === 'SELL') && (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>Số lượng</FieldLabel>
                <FieldInput type="number" value={form.qtyRaw} onChange={e => handleQtyChange(e.target.value)}
                  placeholder={form.type === 'SELL' && matchedAsset ? `Tối đa: ${matchedAsset.quantity.toLocaleString()}` : 'Số cổ phiếu / lô / đơn vị'} />
                {form.type === 'SELL' && matchedAsset && form.quantity && form.quantity > matchedAsset.quantity && (
                  <div style={{ fontSize: 11, color: '#f87171', marginTop: 3 }}>Vượt quá {matchedAsset.quantity.toLocaleString()} đang nắm giữ</div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Giá đơn vị (VNĐ)</FieldLabel>
                <FieldInput value={form.priceRaw} onChange={e => handlePriceChange(e.target.value)} placeholder="VD: 80000, 45tr" />
                {form.price ? <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 3 }}>= {formatVND(form.price)}</div> : null}
              </div>
            </div>
          )}

          {/* Tổng tiền */}
          <div>
            <FieldLabel>Tổng tiền</FieldLabel>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--tx-5)', marginBottom: 4 }}>Tổng thô</div>
                <FieldInput value={form.grossRaw} onChange={e => handleGrossChange(e.target.value)} placeholder="VD: 100 (= 100tr)" />
                {form.gross_amount ? <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 3 }}>= {formatVND(form.gross_amount)}</div> : null}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#f87171', marginBottom: 4 }}>Phí (môi giới, thuế…)</div>
                <FieldInput value={form.feesRaw} onChange={e => handleFeesChange(e.target.value)} placeholder="0" />
                {form.fees ? <div style={{ fontSize: 11, color: '#f87171', marginTop: 3 }}>= {formatVND(form.fees)}</div> : null}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#10b981', marginBottom: 4 }}>Tổng ròng ✱</div>
                <FieldInput value={form.netRaw} onChange={e => handleNetChange(e.target.value)} placeholder="Sau khi trừ phí"
                  style={{ borderColor: (form.net_amount ?? 0) > 0 ? 'rgba(16,185,129,0.4)' : undefined, fontWeight: 600 }} />
                {(form.net_amount ?? 0) > 0 && <div style={{ fontSize: 11, color: '#10b981', marginTop: 3 }}>= {formatVND(form.net_amount!)}</div>}
              </div>
            </div>
          </div>

          {/* Quỹ — chỉ hiện cho TRANSFER (chọn ví nguồn và ví đích per-fund) */}
          {form.type === 'TRANSFER' && realBuckets.length > 0 && (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>Ví nguồn</FieldLabel>
                <select value={form.fund_id || ''} onChange={e => f('fund_id', e.target.value || undefined)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, background: 'var(--bg-input)', border: `1px solid ${!form.fund_id ? 'rgba(248,113,113,0.4)' : 'var(--bd)'}`, color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}>
                  <option value="">— Chọn quỹ nguồn —</option>
                  {realBuckets.map(b => {
                    const wb = computeWalletBalance(b.id, moneyTransactions as MoneyTransaction[]);
                    return <option key={b.id} value={b.id}>{b.name} · Ví: {formatVND(wb)}</option>;
                  })}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Ví đích</FieldLabel>
                <select value={form.dest_fund_id || ''} onChange={e => f('dest_fund_id', e.target.value || undefined)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, background: 'var(--bg-input)', border: `1px solid ${!form.dest_fund_id ? 'rgba(248,113,113,0.4)' : 'var(--bd)'}`, color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}>
                  <option value="">— Chọn quỹ đích —</option>
                  {realBuckets.filter(b => b.id !== form.fund_id).map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {/* Cảnh báo ví chung thiếu vốn khi BUY */}
          {form.type === 'BUY' && (form.net_amount ?? 0) > 0 && globalWalletBalance < (form.net_amount ?? 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', fontSize: 12, color: '#fbbf24' }}>
              ⚠ Ví chung chỉ còn {formatVND(globalWalletBalance)} — cân nhắc nạp thêm vốn (DEPOSIT) trước
            </div>
          )}

          {/* Ngày */}
          <div>
            <FieldLabel>Ngày giao dịch</FieldLabel>
            <FieldInput type="date" value={form.date || today()} onChange={e => f('date', e.target.value)} style={{ maxWidth: 200 }} />
          </div>

          {/* Lý do giao dịch */}
          <div>
            <FieldLabel>
              Lý do giao dịch {(form.type === 'BUY' || form.type === 'SELL') && <span style={{ color: '#f87171' }}>*</span>}
              <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx-5)', marginLeft: 6, textTransform: 'none' }}>
                — dùng cho nhật ký phân tích sau này
              </span>
            </FieldLabel>
            <textarea
              value={form.reason || ''}
              onChange={e => f('reason', e.target.value)}
              rows={3}
              placeholder={
                form.type === 'BUY'  ? 'Tại sao mua? Vd: Định giá hấp dẫn P/E 8x, tăng tỷ trọng cổ phiếu theo SAA...' :
                form.type === 'SELL' ? 'Tại sao bán? Vd: Đạt mục tiêu lợi nhuận +30%, tái cân bằng sang BĐS...' :
                'Ghi lý do hoặc nguồn gốc giao dịch...'
              }
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.6,
                background: 'var(--bg-input)', border: '1px solid var(--bd)',
                color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Ghi chú thêm */}
          <div>
            <FieldLabel>Ghi chú thêm <span style={{ fontWeight: 400, color: 'var(--tx-5)' }}>(tuỳ chọn)</span></FieldLabel>
            <FieldInput value={form.note || ''} onChange={e => f('note', e.target.value)} placeholder="Số hợp đồng, đối tác, điều kiện đặc biệt..." />
          </div>

          {/* Validation error */}
          {validationError && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
              <AlertTriangle size={14} color="#f87171" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: '#f87171', fontWeight: 500 }}>{validationError}</span>
            </div>
          )}

          {/* Checkbox cập nhật số dư tài sản — chỉ hiện khi edit + có tài sản khớp */}
          {editTx && matchedAsset && ['BUY', 'SELL', 'DEPOSIT', 'WITHDRAW'].includes(form.type!) && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 14px', borderRadius: 10, background: applyAssetEffect ? 'rgba(167,139,250,0.06)' : 'var(--bg-card-el)', border: `1px solid ${applyAssetEffect ? 'rgba(167,139,250,0.3)' : 'var(--bd)'}`, transition: 'all 0.15s' }}>
              <input type="checkbox" checked={applyAssetEffect} onChange={e => setApplyAssetEffect(e.target.checked)}
                style={{ marginTop: 2, accentColor: '#a78bfa', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: applyAssetEffect ? '#a78bfa' : 'var(--tx-3)' }}>
                  Cập nhật giá vốn của "{matchedAsset.name}"
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 2 }}>
                  {applyAssetEffect
                    ? 'Sẽ tính lại số lượng và giá vốn TB sau khi lưu'
                    : 'Chỉ cập nhật sổ giao dịch, không thay đổi tài sản'}
                </div>
              </div>
            </label>
          )}

          {/* Preview asset update */}
          {matchedAsset && ['BUY', 'SELL', 'DEPOSIT', 'WITHDRAW'].includes(form.type!) && (form.net_amount ?? 0) > 0 && !validationError && (!editTx || applyAssetEffect) && (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: '#a78bfa', marginBottom: 5 }}>Ảnh hưởng đến số dư — {matchedAsset.name}</div>
              {(() => {
                const net     = form.net_amount!;
                const isAdd   = ['BUY', 'DEPOSIT'].includes(form.type!);
                const newCurr = isAdd ? matchedAsset.currentPrice + net : Math.max(0, matchedAsset.currentPrice - net);
                const newQty  = isAdd
                  ? matchedAsset.quantity + (form.quantity || 0)
                  : Math.max(0, matchedAsset.quantity - (form.quantity || 0));
                return <div style={{ color: 'var(--tx-3)', lineHeight: 1.8 }}>
                  {(form.quantity ?? 0) > 0 && <>Số lượng: <b style={{ color: 'var(--tx-1)' }}>{matchedAsset.quantity.toLocaleString()} → {newQty.toLocaleString()}</b>&nbsp;·&nbsp;</>}
                  Giá hiện tại: <b style={{ color: 'var(--tx-1)' }}>{formatVND(matchedAsset.currentPrice)} → {formatVND(newCurr)}</b>
                  <br/><span style={{ fontSize: 11, color: 'var(--tx-5)' }}>Giá vốn tính từ sổ giao dịch (ledger), không thay đổi trực tiếp</span>
                </div>;
              })()}
            </div>
          )}

          {/* Submit */}
          <button onClick={handleSubmit} disabled={!canSubmit} style={{
            width: '100%', padding: '13px', borderRadius: 12, fontSize: 14, fontWeight: 700,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            background: canSubmit
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'var(--bg-card-el)',
            color: canSubmit ? '#fff' : 'var(--tx-4)',
            border: 'none', transition: 'all 0.15s',
            boxShadow: canSubmit ? '0 4px 16px rgba(16,185,129,0.3)' : 'none',
          }}>
            {submitting ? 'Đang lưu...' : canSubmit
              ? editTx
                ? `Lưu thay đổi · ${formatVND(form.net_amount!)}`
                : `Lưu · ${TYPE_CONFIG[form.type!]?.label}${form.asset_name ? ` — ${form.asset_name}` : ''} · ${formatVND(form.net_amount!)}`
              : validationError ? 'Kiểm tra điều kiện trước khi lưu' : 'Nhập số tiền ròng để tiếp tục'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ── Asset Transaction History (dùng trong AssetManager modal) ─────────────────

type TxEditForm = {
  date: string; reason: string; note: string;
  netRaw: string; grossRaw: string; feesRaw: string; qtyRaw: string; priceRaw: string;
  net_amount: number; gross_amount?: number; fees?: number; quantity?: number; price?: number;
  fund_id?: string;
};

function emptyTxEditForm(tx: MoneyTransaction): TxEditForm {
  return {
    date:         tx.date,
    reason:       tx.reason  || '',
    note:         tx.note    || '',
    net_amount:   tx.net_amount,
    gross_amount: tx.gross_amount,
    fees:         tx.fees,
    quantity:     tx.quantity,
    price:        tx.price,
    fund_id:      tx.fund_id,
    netRaw:   tx.net_amount   ? String(tx.net_amount   / 1_000_000) : '',
    grossRaw: tx.gross_amount ? String(tx.gross_amount / 1_000_000) : '',
    feesRaw:  tx.fees         ? String(tx.fees         / 1_000_000) : '',
    qtyRaw:   tx.quantity     ? String(tx.quantity)                  : '',
    priceRaw: tx.price        ? String(tx.price        / 1_000_000) : '',
  };
}

export function AssetTransactionHistory({ asset, onNavigateToTx }: { asset: Asset; onNavigateToTx?: (txId: string) => void }) {
  const { state, dispatch } = useAppState();
  const { moneyTransactions = [], buckets = [] } = state;
  const realBuckets = (buckets as Bucket[]).filter(b => b.id !== 'ruong-chua');

  const [editingTx, setEditingTx] = useState<MoneyTransaction | null>(null);
  const [ef, setEf]               = useState<TxEditForm | null>(null);

  const allTxs = useMemo(() =>
    (moneyTransactions as MoneyTransaction[])
      .filter(t => t.asset_id === asset.id
        || t.asset_name?.toLowerCase() === asset.name.toLowerCase()
        || t.asset_name?.toLowerCase() === asset.code.toLowerCase())
      .sort((a, b) => b.date.localeCompare(a.date)),
    [moneyTransactions, asset.id, asset.name, asset.code]
  );

  const txs = useMemo(() => allTxs.slice(0, 20), [allTxs]);

  // Tính giá vốn đúng từ giao dịch (nguồn sự thật)
  const computedCostPrice = useMemo(() => {
    const deposited = allTxs.filter(t => ['BUY', 'DEPOSIT'].includes(t.type)).reduce((s, t) => s + t.net_amount, 0);
    const withdrawn = allTxs.filter(t => ['SELL', 'WITHDRAW'].includes(t.type)).reduce((s, t) => s + t.net_amount, 0);
    return Math.max(0, deposited - withdrawn);
  }, [allTxs]);

  const hasDrift = allTxs.length > 0 && Math.abs(computedCostPrice - asset.costPrice) > 1;

  const handleReconcile = () => {
    const priceParity = asset.currentPrice === asset.costPrice; // nếu giá hiện tại bám giá vốn thì cập nhật cả hai
    dispatch({ type: 'UPDATE_ASSET', payload: {
      ...asset,
      costPrice:    computedCostPrice,
      currentPrice: priceParity ? computedCostPrice : asset.currentPrice,
    }});
  };

  const openEdit = (tx: MoneyTransaction) => { setEditingTx(tx); setEf(emptyTxEditForm(tx)); };
  const closeEdit = () => { setEditingTx(null); setEf(null); };

  const fef = <K extends keyof TxEditForm>(k: K, v: TxEditForm[K]) =>
    setEf(p => p ? { ...p, [k]: v } : p);

  const handleNetChange = (raw: string) => {
    fef('netRaw', raw);
    fef('net_amount', parseRawAmount(raw));
  };
  const handleGrossChange = (raw: string) => {
    fef('grossRaw', raw);
    const gross = parseRawAmount(raw);
    fef('gross_amount', gross || undefined);
    if (gross && ef) {
      const net = Math.max(0, gross - (ef.fees || 0));
      setEf(p => p ? { ...p, grossRaw: raw, gross_amount: gross, net_amount: net, netRaw: String(net / 1_000_000) } : p);
    }
  };
  const handleFeesChange = (raw: string) => {
    fef('feesRaw', raw);
    const fees = parseRawAmount(raw);
    fef('fees', fees || undefined);
    if (ef) {
      const net = Math.max(0, (ef.gross_amount || 0) - fees);
      if (ef.gross_amount) setEf(p => p ? { ...p, feesRaw: raw, fees, net_amount: net, netRaw: String(net / 1_000_000) } : p);
    }
  };

  const handleSave = () => {
    if (!editingTx || !ef || ef.net_amount <= 0) return;
    const updated: MoneyTransaction = {
      ...editingTx,
      date:         ef.date,
      net_amount:   ef.net_amount,
      gross_amount: ef.gross_amount,
      fees:         ef.fees,
      quantity:     ef.quantity,
      price:        ef.price,
      fund_id:      ef.fund_id,
      reason:       ef.reason,
      note:         ef.note || undefined,
    };
    dispatch({ type: 'UPDATE_MONEY_TX', payload: updated });

    // Khi sửa Giao dịch gốc → áp delta vào costPrice + currentPrice của tài sản
    if (editingTx.reason === 'Giao dịch gốc') {
      const delta = ef.net_amount - editingTx.net_amount;
      if (delta !== 0) {
        dispatch({ type: 'UPDATE_ASSET', payload: {
          ...asset,
          costPrice:    Math.max(0, asset.costPrice    + delta),
          currentPrice: Math.max(0, asset.currentPrice + delta),
        }});
      }
    }

    closeEdit();
  };

  if (txs.length === 0) return null;

  const cfg = editingTx ? TYPE_CONFIG[editingTx.type] : null;

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--bd)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Lịch sử giao dịch ({txs.length})
      </div>

      {/* ── Cảnh báo lệch giá vốn ── */}
      {hasDrift && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.3)', marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fbbf24' }}>Giá vốn không khớp giao dịch</div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>
              Sổ giao dịch: <b style={{ color: 'var(--tx-2)' }}>{formatVND(computedCostPrice)}</b>
              {' · '}Giá vốn hiện tại: <b style={{ color: 'var(--tx-2)' }}>{formatVND(asset.costPrice)}</b>
              {' · '}Chênh: <b style={{ color: computedCostPrice > asset.costPrice ? '#10b981' : '#f87171' }}>{formatVND(Math.abs(computedCostPrice - asset.costPrice))}</b>
            </div>
          </div>
          <button onClick={handleReconcile}
            style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', whiteSpace: 'nowrap' }}>
            Đồng bộ lại
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {txs.map(tx => {
          const c = TYPE_CONFIG[tx.type];
          const { Icon } = c;
          const fund = (buckets as Bucket[]).find(b => b.id === tx.fund_id);
          return (
            <div key={tx.id}
              onClick={() => openEdit(tx)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                borderRadius: 9, background: 'var(--bg-card-el)', border: '1px solid var(--bd-sub)',
                cursor: 'pointer', transition: 'background 0.12s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-row-hover)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-card-el)'; }}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={13} color={c.color} strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: c.color }}>{c.label}</span>
                  <span style={{ fontSize: 10, color: 'var(--tx-5)' }}>{tx.date}</span>
                  {fund && <span style={{ fontSize: 10, color: 'var(--tx-5)' }}>· {fund.name}</span>}
                </div>
                {tx.quantity && tx.price && (
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>
                    {tx.quantity.toLocaleString()} × {formatVND(tx.price)}
                  </div>
                )}
                {tx.reason && (
                  <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.reason}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.color }}>
                  {c.sign !== '↔' ? c.sign : ''}{formatVND(tx.net_amount)}
                </div>
                {tx.fees ? <div style={{ fontSize: 10, color: 'var(--tx-5)' }}>phí {formatVND(tx.fees)}</div> : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Inline Edit Modal ── */}
      <Modal open={!!editingTx} onClose={closeEdit}
        title={cfg ? `Sửa · ${cfg.label}` : 'Sửa giao dịch'}
        subtitle={editingTx?.asset_name ? `Tài sản: ${editingTx.asset_name}` : asset.name}
        size="sm">
        {editingTx && ef && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Badge loại */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <TypeBadge type={editingTx.type} />
              {onNavigateToTx && (
                <button onClick={() => { closeEdit(); onNavigateToTx(editingTx.id); }}
                  style={{ fontSize: 11, color: 'var(--tx-4)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                  Xem trong sổ giao dịch ↗
                </button>
              )}
            </div>

            {/* Ngày */}
            <div>
              <FieldLabel>Ngày</FieldLabel>
              <FieldInput type="date" value={ef.date} onChange={e => fef('date', e.target.value)} style={{ maxWidth: 180 }} />
            </div>

            {/* Tiền */}
            <div>
              <FieldLabel>Tổng tiền</FieldLabel>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--tx-5)', marginBottom: 4 }}>Tổng thô</div>
                  <FieldInput value={ef.grossRaw} onChange={e => handleGrossChange(e.target.value)} placeholder="tuỳ chọn" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#f87171', marginBottom: 4 }}>Phí</div>
                  <FieldInput value={ef.feesRaw} onChange={e => handleFeesChange(e.target.value)} placeholder="0" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#10b981', marginBottom: 4 }}>Tổng ròng ✱</div>
                  <FieldInput value={ef.netRaw} onChange={e => handleNetChange(e.target.value)}
                    style={{ borderColor: ef.net_amount > 0 ? 'rgba(16,185,129,0.4)' : undefined, fontWeight: 600 }} />
                  {ef.net_amount > 0 && <div style={{ fontSize: 11, color: '#10b981', marginTop: 3 }}>= {formatVND(ef.net_amount)}</div>}
                </div>
              </div>
            </div>

            {/* Số lượng + Giá (nếu có) */}
            {['BUY', 'SELL'].includes(editingTx.type) && (
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <FieldLabel>Số lượng</FieldLabel>
                  <FieldInput type="number" value={ef.qtyRaw}
                    onChange={e => { fef('qtyRaw', e.target.value); fef('quantity', parseFloat(e.target.value) || undefined); }} />
                </div>
                <div style={{ flex: 1 }}>
                  <FieldLabel>Giá đơn vị</FieldLabel>
                  <FieldInput value={ef.priceRaw}
                    onChange={e => { fef('priceRaw', e.target.value); fef('price', parseRawAmount(e.target.value) || undefined); }} />
                </div>
              </div>
            )}

            {/* Quỹ */}
            {realBuckets.length > 0 && (
              <div>
                <FieldLabel>Quỹ liên quan</FieldLabel>
                <select value={ef.fund_id || ''} onChange={e => fef('fund_id', e.target.value || undefined)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}>
                  <option value="">— Không gán —</option>
                  {realBuckets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            {/* Lý do */}
            <div>
              <FieldLabel>Lý do giao dịch</FieldLabel>
              <textarea value={ef.reason} onChange={e => fef('reason', e.target.value)} rows={2}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.5, background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            {/* Ghi chú */}
            <div>
              <FieldLabel>Ghi chú <span style={{ fontWeight: 400, color: 'var(--tx-5)' }}>(tuỳ chọn)</span></FieldLabel>
              <FieldInput value={ef.note} onChange={e => fef('note', e.target.value)} placeholder="Thêm ghi chú..." />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
              <button onClick={closeEdit}
                style={{ padding: '9px 18px', borderRadius: 10, fontSize: 13, cursor: 'pointer', background: 'var(--bg-card-el)', border: '1px solid var(--bd)', color: 'var(--tx-3)' }}>
                Hủy
              </button>
              <button onClick={handleSave} disabled={ef.net_amount <= 0}
                style={{ padding: '9px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: ef.net_amount > 0 ? 'pointer' : 'not-allowed', background: ef.net_amount > 0 ? 'linear-gradient(135deg,#10b981,#059669)' : 'var(--bg-card-el)', color: ef.net_amount > 0 ? '#fff' : 'var(--tx-5)', border: 'none' }}>
                Lưu thay đổi
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
